import React from 'react';
import { Users, Award, Target, TrendingUp } from 'lucide-react';
import { mockData } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';

export const About = () => {
  const iconMap = {
    'Students Trained': Users,
    'Pass Rate': Award,
    'First Attempt Pass Rate': Award,
    'Years of Experience': TrendingUp,
    'Certified Instructors': Target
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="About Us - Liverpool's Top Driving School"
        description="About Top Driver Driving School - Liverpool's premier driving school with 14+ years of experience, 10,000+ students trained, 95% pass rate. Professional driving instruction from certified experts since 2010."
        keywords="about driving school Liverpool, driving instructors Liverpool, certified instructors Liverpool, Liverpool driving school experience"
        canonical="https://topdriver.co.uk/about"
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h1 className="text-5xl sm:text-6xl font-bold text-black mb-6">
              About Top Driver
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {mockData.aboutInfo.mission}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
            {mockData.aboutInfo.stats.map((stat) => {
              const Icon = iconMap[stat.label] || Award;
              return (
                <Card key={stat.label} className="border-2 border-gray-200 hover:border-red-600 transition-colors duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-3xl font-bold text-black mb-1">{stat.value}</p>
                    <p className="text-sm text-gray-600">{stat.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-black mb-8 text-center">Our Story</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            {mockData.aboutInfo.story}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            What started as a small family-owned driving school has grown into one of the region's most trusted names in driver education. Our commitment to excellence and personalized instruction has remained constant throughout our growth.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Every member of our team shares a passion for teaching and a dedication to road safety. We believe that good driving instruction goes beyond just passing a test—it's about building lifelong safe driving habits.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-black mb-12 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-3">Excellence</h3>
                <p className="text-gray-600 leading-relaxed">
                  We maintain the highest standards in driver education, from our instructors to our vehicles.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-3">Safety First</h3>
                <p className="text-gray-600 leading-relaxed">
                  Your safety is our top priority. Every lesson focuses on defensive driving and hazard awareness.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-3">Student-Centered</h3>
                <p className="text-gray-600 leading-relaxed">
                  We adapt our teaching methods to each student's learning style and comfort level.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;